// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2024-11-02 13:11:29 +0100 (Sat, 02 Nov 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 147139 $
//  Purpose   : viewer react-hooks
// =============================================================================
// ---Third-party imports
import React, {createContext, useContext} from 'react'

// ---Framework imports
import {useApi, useEffect, useState, useSelector, useActions, useMemo, useCallback} from 'uix/hooks'
import {get, dict, isEqual, uniq, flatten, isArray} from 'ldash'
import log from 'uix/logging'
import {Actions} from 'uix/registries'

// ---Exports
export const ViewerContext = createContext({})
export const useViewer = () => useContext(ViewerContext)

// read structuredImageLocs into flat list but preserve parent ids
export const getPagesFlat = (parent, level, path = []) =>
  flatten(
    (parent.children || [])
      .filter(e => !e.link)
      .map(e =>
        e.datatype == 3
          ? {...e, pid: parent.id, level, parent, path}
          : getPagesFlat(e, level + 1, [...path, e.id])
      )
  )

export const indexImageLocs = (imageLocs, focusOtid) => {
  if (!imageLocs) {
    return {
      pages: [],
      byId: {},
      pageIds: [],
    }
  }
  let pages = getPagesFlat(imageLocs, 0)

  // byId and flatIdx are not affected by focusOtid
  let byId = dict(pages.map((p, i) => [p.id, {...p, flatIdx: i}]))

  // XXX: p.path.includes looks pretty slow
  if (focusOtid && focusOtid != imageLocs.root_id) {
    let collecting = false
    let collected = []
    for (let p of pages) {
      if (collecting && !p.path.includes(focusOtid)) {
        break
      }
      if (!collecting && p.path.includes(focusOtid)) {
        collecting = true
      }
      if (collecting) {
        collected.push(p)
      }
    }
    pages = collected
  }

  return {
    pageIds: pages.map(p => p.id),
    pages,
    byId,
  }
}

export const useImageLocs = ({otid, enabled = true}) => {
  /* given a root id, fetch imagelocs tree and persist in redux store
  also see useStructuredImageLocations
  */
  let imageLocs = useSelector(root => root.data.viewer.imageLocsById[otid])
  let callAndReduce = useActions('reduceApi')

  let reducer = (dataState, result, error) => {
    let {imageLocsById = {}} = dataState.viewer
    if (result) {
      return {
        viewer: {...dataState.viewer, imageLocsById: {...imageLocsById, [otid]: result.imageLocs}},
      }
    }
  }

  useEffect(() => {
    if (enabled && !imageLocs)
      callAndReduce('filter/structuredImageLocationsJson', {id: otid}, reducer, {
        silent: true,
      })
  }, [enabled, otid])
  return imageLocs
}

export const useIndexedImageLocs = ({otid, focusOtid, enabled = true}) => {
  let imageLocs = useImageLocs({otid, enabled})
  return useMemo(() => indexImageLocs(imageLocs, focusOtid), [imageLocs])
}

export const useImageLocsPageSet = ({vid, otids, enabled = true}) => {
  let joined_otids = (otids || []).join(',')
  // XXX: probably move to root.data.viewer
  let imageLocs = useSelector(root => root.data.imageLocsPageSet?.[joined_otids])
  let callAndReduce = useActions('reduceApi')

  let reducer = (dataState, result, error) => {
    let {imageLocsPageSet = {}} = dataState
    if (result) {
      // compat with structuredImageLocsJSON
      let l = result.imageLocs.map(
        ({otid, otlink, caption, width, height, dpi, odid, datatype, subtype, ocr, pbox}) => ({
          id: otid, // also support links
          link_id: otlink, // contains physical id for download/webcache
          caption,
          // broken images return a 0,0 and will crash the viewer
          // maybe resolve this situation more formally
          size: [width || 1200, height || 1800],
          dpi: dpi || 300, // broken dpi (null) will also crash the viewer
          odid,
          datatype,
          type: subtype,
          pbox,
          ocr, // important for wordlayer
        })
      )
      return {imageLocsPageSet: {...imageLocsPageSet, [joined_otids]: l}}
    }
  }

  useEffect(() => {
    if (enabled && joined_otids && !imageLocs)
      callAndReduce('getImageLocsPageSet', {otids: joined_otids}, reducer, {
        silent: false,
        method: 'post',
      })
  }, [vid, joined_otids, enabled])

  // add pseudo root
  return useMemo(() => (imageLocs ? {id: vid, datatype: null, children: imageLocs} : {}), [
    imageLocs,
  ])
}

export const useWordData = otid => {
  let wordData = useSelector(root => get(root.data.wordDataById, otid))
  let callAndReduce = useActions('reduceApi')

  let reducer = (dataState, result, error) => {
    let {wordDataById = {}} = dataState
    if (result) {
      return {wordDataById: {...wordDataById, [otid]: result}}
    } // else error
  }

  useEffect(() => {
    if (!wordData && otid)
      callAndReduce('getWordData', {id: otid}, reducer, {
        silent: true,
      })
  }, [])

  return wordData
}

export const useInbookSearch = ({otid, query, vid = null}) => {
  let searchData = useSelector(root => get(root.data.viewer.inBookSearchById, otid, {})[query])
  let activeInBookSearch = useSelector(root => root.data.viewer.activeInBookSearches[vid])

  let callAndReduce = useActions('reduceApi')
  let setInbookSearch = useActions('setInbookSearch')

  let reducer = (dataState, result, error) => {
    if (result) {
      let {viewer} = dataState
      let {inBookSearchById, activeInBookSearches} = viewer

      let queries = get(inBookSearchById, otid, {})
      result = {...result, query, pageById: dict(result.searchResult.otids.map(otid => [otid, {}]))}
      queries = {...queries, [query]: result}
      inBookSearchById = {...inBookSearchById, [otid]: queries}
      activeInBookSearches = {...activeInBookSearches, [vid]: result}
      return {viewer: {...viewer, activeInBookSearches, inBookSearchById}}
    } // else error
  }

  useEffect(() => {
    if (!query || query.length < 3) return

    if (!searchData) {
      // addTerms only for legacy compat
      callAndReduce('fulltextSearch', {otid, query, addTerms: true}, reducer, {
        silent: false,
      })
    } else if (!isEqual(activeInBookSearch, searchData)) {
      setInbookSearch(vid, searchData)
    }
  }, [otid, query])

  return searchData
}

export const useFulltextHits = ({vid, otid}) => {
  let searchData = useSelector(root => root.data.viewer.activeInBookSearches[vid])
  let callAndReduce = useActions('reduceApi')

  let {termsQueryKey} = searchData.searchResult
  let pageData = searchData.pageById[otid]

  let reducer = (dataState, result, error) => {
    //~ return {}
    let {viewer} = dataState
    if (result?.[otid]) {
      let {activeInBookSearches} = viewer
      // add hits from result
      let pageById = {...activeInBookSearches[vid].pageById, [otid]: {hits: result[otid].hits}}
      let activeInBookSearch = {...activeInBookSearches[vid], pageById}
      activeInBookSearches = {...activeInBookSearches, [vid]: activeInBookSearch}

      return {viewer: {...viewer, activeInBookSearches}}
    } // else error
  }

  useEffect(() => {
    //~ log.x(termsQueryKey, pageData.hits)
    if (termsQueryKey && !pageData.hits) {
      callAndReduce('extractFulltextCoords', {otids: otid, termsQueryKey}, reducer, {
        silent: false,
      })
    }
  }, [otid, termsQueryKey])

  return pageData.hits
}

export const useI3F = ({manifest}) => {
  let data = useSelector(root => root.data.i3f.manifestById[manifest])
  let getI3F = useActions('getI3F')
  useEffect(() => {
    if (!data && manifest) {
      getI3F({manifest})
    }
  }, [manifest])

  return data || {}
}

export const useGeometryCalculation = (vid, calculate, deps) => {
  /* low level geometry drawing
  recalculate geometry on deps change and save in store for
  viewer consumption via geometry layer
   */
  let setViewerGeometry = useActions(Actions.setViewerGeometry)

  // XXX: why does this unmount handling trigger often if we use it in the next useEffect
  useEffect(() => {
    return () => setViewerGeometry(vid) // reset
  }, [])

  useEffect(() => {
    setViewerGeometry(vid, calculate(), true)
  }, [...deps])
}

export const useGeometryInteraction = ({vid, otid, geomtype}) => {
  /* interact with geometry
   */
  let selected = useSelector(root => root.data.viewer.selectedGeometry)
  let modified = useSelector(root => root.data.viewer.modifiedGeometry)
  let k = `${vid}_${otid}`
  // return all if geomtype missing?
  modified = modified[k]?.[geomtype] || {}
  selected = selected[vid]?.[otid]?.[geomtype] || []

  let setModifiedViewerGeometry = useActions(Actions.setModifiedViewerGeometry)
  let resetModified = useCallback(() => setModifiedViewerGeometry(vid, otid, geomtype, null))
  return {selected, modified, resetModified}
}

const normalizeBBox = bbox =>
  isArray(bbox) ? {x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3]} : bbox

export const useBoundingBoxGeometry = ({vid, otid, bbox, geomtype = 'pagebox', isModified}) => {
  /* simple one page / one bounding box case
   */

  let id = 'bbox1'
  // normalize from [x1, y1, x2, y2] form
  bbox = useMemo(() => normalizeBBox(bbox), [bbox])

  let {modified, resetModified} = useGeometryInteraction({vid, otid, geomtype})
  useGeometryCalculation(vid, () => ({[otid]: {[geomtype]: [{bbox, id, modified: isModified}]}}), [
    bbox,
    geomtype,
    isModified,
  ])

  modified = useMemo(() => modified[id], [modified])
  return {modified, resetModified}
}

export const useHighlightingGeometry = ({vid, otid, bboxes, geomtype = 'pagebox'}) => {
  /* one page / one bounding box case
   */
  let {modified, resetModified} = useGeometryInteraction({vid, otid, geomtype})
  useGeometryCalculation(
    vid,
    () => ({[otid]: {[geomtype]: bboxes.map((bbox, i) => ({bbox: normalizeBBox(bbox), id: i}))}}),
    [bboxes, geomtype]
  )
}

export const useStructuredImageLocations = ({otid}) => {
  /* given a otid from a book structure, return indexed tree
  also return prevPage and nextPage relative to given otid

  see useImageLocs for other version
   */
  let [activeData, setActiveData] = useState(null)

  // disable if otid is part of loaded tree
  let enabled = activeData === null || !activeData.byId[otid]
  let {data, loading} = useApi(
    'filter/structuredImageLocationsJson',
    {id: otid},
    {condition: enabled}
  )
  useEffect(() => {
    if (data) {
      if (data.imageLocs) {
        setActiveData({
          tree: data.imageLocs,
          ...indexImageLocs(data.imageLocs),
        })
      } else {
        // bad data?
        setActiveData(null)
      }
    }
  }, [data])

  // calculate next/prev page ids (only if incoming otid is page id)
  let structuredImageLocations = useMemo(() => {
    if (!activeData) return activeData
    let pageIdx = activeData.pageIds.indexOf(otid)
    let prevPage = activeData.pageIds[pageIdx - 1] ?? null
    let nextPage = pageIdx > -1 ? activeData.pageIds[pageIdx + 1] ?? null : null
    return {...activeData, prevPage, nextPage}
  }, [activeData, otid])

  return {structuredImageLocations, loading}
}
